import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as EmployeeManagementActions from './employee-management.actions';
import { TEmployeeManagementActionType } from './employee-management.models';
import { FactoryFacade } from '../factory/factory.facade';
import { EmployeeManagementFacade } from './employee-management.facade';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';

@Injectable({ providedIn: 'root' })
export class EmployeeListEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private factoryFacade: FactoryFacade,
    private employeeManagementFacade: EmployeeManagementFacade,
    private APIHelper: AppNotificationUtilClass,
  ) {}

  getEmployeeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeManagementActionType.GetEmployeeList),
      switchMap((action: EmployeeManagementActions.GetEmployeeListInfo) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.employeeManagementFacade.getPagination().pipe(
          take(1),
          map((pagination) => {
            return [...args, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId, pagination]) =>
        this.mmDataService
          .fetchEmployeeListData({
            factoryId,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
            selectedFilters: action.selectedFilters,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new EmployeeManagementActions.GetEmployeeListInfoSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new EmployeeManagementActions.GetEmployeeListInfoFailure());
            }),
          ),
      ),
    ),
  );

  updateEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeManagementActionType.UpdateEmployee),
      switchMap((action: EmployeeManagementActions.UpdateEmployee) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.updateEmployee(action.payload)).pipe(
          finalize(() => this.APIHelper.dismissLoadingV2(loaderEl)),
          map(() => {
            this.employeeManagementFacade.resetEmployeeListInfo('ACTION');
            return new EmployeeManagementActions.UpdateEmployeeSuccess();
          }),
          catchError(() => {
            return of(new EmployeeManagementActions.UpdateEmployeeFailure());
          }),
        ),
      ),
    ),
  );

  deleteEmployeeReplacements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeManagementActionType.DeleteEmployeeReplacements),
      switchMap((action: EmployeeManagementActions.DeleteEmployeeReplacements) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.getEmployeeReplacements({ employeeId: action.employeeId })).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new EmployeeManagementActions.DeleteEmployeeReplacementsSuccess(res);
          }),
          catchError(() => {
            return of(new EmployeeManagementActions.DeleteEmployeeReplacementsFailure());
          }),
        ),
      ),
    ),
  );

  deleteEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeManagementActionType.DeleteEmployee),
      switchMap((action: EmployeeManagementActions.DeleteEmployee) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        from(
          this.mmDataService.deleteEmployee({
            factoryId,
            employeeIdToDelete: action.payload.employeeIdToDelete,
            employeeReplacements: action.payload.employeeReplacements,
          }),
        ).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.employeeManagementFacade.resetEmployeeListInfo('ACTION');
            return new EmployeeManagementActions.DeleteEmployeeFailure();
          }),
          catchError(() => {
            return of(new EmployeeManagementActions.DeleteEmployeeFailure());
          }),
        ),
      ),
    ),
  );
}
